import React from "react";
import {
  FaThLarge,
  FaCheckDouble,
  FaPhone,
  FaFont,
  FaListUl,
  FaEye,
} from "react-icons/fa";
import { FaMoneyBillTransfer } from "react-icons/fa6";

// Icon keys are shipped by the game master (app and web masters have
// different powerup sets) — unknown keys fall back to label-only.
export const POWERUP_ICONS = {
  "th-large": FaThLarge,
  "check-double": FaCheckDouble,
  phone: FaPhone,
  call: FaPhone,
  font: FaFont,
  choices: FaListUl,
  swap: FaMoneyBillTransfer,
};

/** Small count pill pinned to a vote target's corner. */
export const VoteCount = ({ count }) =>
  count > 0 ? (
    <span className="absolute -top-2 -left-2 min-w-5 h-5 px-1 rounded-full bg-mainText text-header text-xs font-bold flex items-center justify-center z-20 shadow">
      {count}
    </span>
  ) : null;

/** Ring/locked classes for a vote target. */
export const voteTargetClasses = (mine, canVote) =>
  `${mine ? "ring-4 ring-mainText" : ""} ${
    canVote ? "" : "saturate-50 opacity-80 cursor-not-allowed"
  }`;

export const TeamAvatar = ({ team, className }) =>
  team?.image ? (
    <img
      src={team.image}
      alt={team?.name}
      className={className || "w-full h-full object-cover"}
    />
  ) : (
    <div className="w-full h-full bg-secondaryButton flex items-center justify-center text-white font-bold">
      {(team?.name || "؟").charAt(0)}
    </div>
  );

/**
 * Side team rail — same markup as the rail in components/tarbaa/Question.js.
 * On the question screen the turn team's unused powerups are vote targets.
 */
export const TeamRail = ({ state, presence, voting, interactive }) => {
  const teams = state?.teams || [];
  const defs = state?.powerUps?.defs || [];
  const perTeam = state?.powerUps?.perTeam || {};

  return (
    <div className="portrait:h-1/4 portrait:w-full h-1/4 w-full sm:w-1/4 sm:h-full flex-row sm:flex-col portrait:flex-row flex justify-center items-center text-white text-4xl sm:pl-4 sm:gap-2 portrait:pt-6 pt-6 sm:pt-0">
      {teams.map((team) => {
        const isTurn = String(team.id) === String(state?.turnTeamId);
        const used = perTeam[team.id] || {};
        return (
          <div
            className="portrait:w-1/4 portrait:h-full w-full h-1/4 px-[1px] xl:p-2"
            key={team.id}
          >
            <div
              className={`portrait:flex-col w-full h-full flex flex-col sm:flex-row justify-center items-center bg-[#dd9c6e] rounded-lg ${
                isTurn ? "border-4 border-dark" : ""
              }`}
            >
              <div className="portrait:w-full portrait:h-1/2 w-full h-1/2 sm:w-1/3 sm:h-full flex justify-center items-center p-1 sm:p-2">
                <div className="h-full aspect-square rounded-full overflow-hidden flex justify-center items-center relative">
                  <div className="w-full aspect-square rounded-full overflow-hidden">
                    <TeamAvatar team={team} />
                  </div>
                </div>
              </div>
              <div className="h-1/2 w-full sm:w-2/3 sm:h-full text-sm md:text-xl xl:text-2xl font-bold">
                <div className="h-1/2 flex items-center justify-center overflow-hidden gap-1">
                  <h1 className="text-white text-center">{team.name}</h1>
                  <span className="flex items-center gap-0.5 text-xs text-white/80 font-normal">
                    <FaEye className="w-3 h-3" />
                    {presence?.byTeam?.[team.id] || 0}
                  </span>
                </div>
                <div className="portrait:w-full portrait:h-1/2 w-full h-1/2 flex justify-center items-center p-2 gap-1 md:gap-4">
                  {defs.map((def) => {
                    const Icon = POWERUP_ICONS[def.icon];
                    const isUsed = !!used[def.name];
                    const key = `powerup:${def.name}`;
                    const votable = interactive && isTurn && !isUsed;
                    const count = votable ? voting.counts?.[key] || 0 : 0;
                    const mine = votable && voting.myVote === key;
                    return (
                      <button
                        key={def.name}
                        title={def.label}
                        disabled={!votable}
                        onClick={() => votable && voting.tap("question", key)}
                        className={`relative bg-mainButtonShadow hover:bg-mainButton rounded-md md:rounded-lg p-1 md:p-2 text-sm md:text-lg xl:text-2xl disabled:bg-[#94674690] disabled:text-[#ffffff90] ${
                          votable ? voteTargetClasses(mine, voting.canVote) : ""
                        }`}
                      >
                        {votable && <VoteCount count={count} />}
                        {Icon ? <Icon /> : <span className="text-xs">{def.label}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
